import { isObject } from './statusMatchTypes'
import type { StatusMatchEnrolledModalCopy, StatusMatchLabPayload } from './statusMatchTypes'

/** Mirrors Lab's global modal pop-up route blacklist. Always applied — campaigns can only add to it. */
export const DEFAULT_ENROLLED_MODAL_ROUTE_BLACKLIST: ReadonlyArray<string> = [
  '/',
  '/checkout/*',
  '/trip-planner/*',
  '/support/*',
]

const COPY_KEYS: ReadonlyArray<keyof StatusMatchEnrolledModalCopy> = [
  'headline',
  'description',
  'startsNowTitle',
  'startsNowSubtitle',
  'stepsTitle',
  'benefitsTitle',
  'ctaText',
  'ctaUrl',
]

function isSiteRelativePath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

/**
 * Returns the enrolled modal copy when the campaign has the modal enabled, otherwise undefined.
 * `-i18n` siblings are already resolved with the rest of the campaign payload.
 * Keeps known non-empty string fields only, so a malformed field can't break the modal.
 */
export function getEnrolledModalCopy(config: StatusMatchLabPayload): StatusMatchEnrolledModalCopy | undefined {
  const modal: unknown = config.enrolledModal
  if (!isObject(modal) || modal.enabled === false) return undefined

  const copy: StatusMatchEnrolledModalCopy = {}
  for (const key of COPY_KEYS) {
    const field = modal[key]
    if (typeof field === 'string' && field.length > 0) {
      copy[key] = field
    }
  }
  // Only same-site paths — a full URL, protocol-relative or javascript: value would break the router link
  if (copy.ctaUrl && !isSiteRelativePath(copy.ctaUrl)) {
    delete copy.ctaUrl
  }
  return copy
}

/** Route patterns where the campaign's enrolled modal must not show: the defaults plus any the campaign adds */
export function getEnrolledModalRouteBlacklist(config: StatusMatchLabPayload): ReadonlyArray<string> {
  const routeBlacklist: unknown = config.enrolledModal?.routeBlacklist
  if (!Array.isArray(routeBlacklist)) return DEFAULT_ENROLLED_MODAL_ROUTE_BLACKLIST
  return [
    ...DEFAULT_ENROLLED_MODAL_ROUTE_BLACKLIST,
    ...routeBlacklist.filter((pattern): pattern is string => typeof pattern === 'string'),
  ]
}
